import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatPaginator} from '@angular/material/paginator';
import {MatTableDataSource} from '@angular/material/table';
import {Router} from '@angular/router';
import {MatSort} from '@angular/material/sort';
import {ReportEvent} from 'src/app/model/report.model';
import {ROUTES} from 'src/app/route/routes';
import {ModalErrorComponent} from 'src/app/modal/modal-gestioneErrori/modal-error/modal-error.component';
import {MyErrorHandler} from 'src/app/constants/error';
import {MatDialog} from '@angular/material/dialog';
import * as XLSX from 'xlsx';
import {EventService} from 'src/app/services/event.service';
import {ReservationService} from 'src/app/services/reservation.service';
import {Event, MacroEvent, Ship} from 'src/app/model';
import {displayedColumns_event, xlsxColumns_event} from 'src/app/constants/xls-columns';
import {FormControl, FormGroup} from '@angular/forms';
import {SpinnerService} from 'src/app/_components/spinner/spinner.service';
import {UserService} from "../../../services/user.service";
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

@Component({
  selector: 'app-reservation-report',
  templateUrl: './reservation-report.component.html',
  styleUrls: ['./reservation-report.component.scss'],
  standalone: false
})

export class ReservationReportComponent implements OnInit, AfterViewInit, OnDestroy {

  private ngUnsubscribe = new Subject<void>();
  allEvents: Array<Event | MacroEvent> = [];
  eventSelected: Event | MacroEvent | null = null;
  availableShips: Array<Ship> = [];
  selectedShip: Ship | null = null;

  range = new FormGroup({
    start: new FormControl(),
    end: new FormControl(),
  });

  private allData_event: Array<ReportEvent> = [];
  private event_name_after_search: string | undefined;
  displayedColumns: string[] = [];
  private xlsxColumns: string[][] = [[]];
  dataSource = new MatTableDataSource<ReportEvent>();
  btnDownload: boolean = false;
  viewReservations: boolean = false;
  minDate: Date = new Date();
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private eventService: EventService,
    private reservationService: ReservationService,
    private spinner: SpinnerService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.availableShips = this.userService.ships;

    if (this.availableShips.length === 1) {
      this.selectedShip = this.availableShips[0];
      this.onShipSelected();
    }
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  trackByShipId = (_: number, s: Ship) => s.id_ship;

  trackByEventId = (_: number, e: Event | MacroEvent) => e.id_selector;

  onShipSelected() {
    this.eventSelected = null;
    this.allEvents = [];
    this.dataSource.data = [];
    this.viewReservations = false;
    this.btnDownload = false;
    this.range.reset();

    if (this.selectedShip) {
      this.getFilteredEvents(this.selectedShip.id_ship);
    }
  }

  getFilteredEvents(shipId: string) {
    this.spinner.show();
    this.eventService.getAllEvents(true, shipId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res) => {
          const newEvents: Event[] = res.map(raw => {
            const ev = new Event();
            ev.convertToFE(raw);
            return ev;
          });

          const macroEvents: MacroEvent[] = [
            new MacroEvent('events_spa', 'Tutti gli eventi spa'),
            new MacroEvent('events_tender', 'Tutti gli eventi tender')
          ];

          this.allEvents = [...macroEvents, ...newEvents];
        },

        error: (err) => {
          this.showError(err);
          this.spinner.hide();
        },
        complete: () => {
          this.spinner.hide();
        }
      });
  }

  search() {
    if (!this.eventSelected || !this.selectedShip) return;
    this.spinner.show();
    this.displayedColumns = displayedColumns_event;
    this.xlsxColumns = xlsxColumns_event;
    let search_id = this.eventSelected.id_selector;
    this.event_name_after_search = this.eventSelected.name_selector;
    let { start, end } = this.range.value;

    const formatDate = (date: any) => {
      if (!date) return undefined;
      // This logic seems specific to your date object structure
      const d = date._i;
      return `${d.year}-${String(d.month + 1).padStart(2, '0')}-${String(d.date).padStart(2, '0')}`;
    };


    const date_from = formatDate(start);

    const date_to = formatDate(end);

    this.reservationService.getReservationsByEvent(this.selectedShip.id_ship,search_id!, date_from, date_to)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res) => {
          this.allData_event = res.map((el: any) => new ReportEvent(
            el.event_name,
            el.creation_datetime,
            el.occurrence_datetime,
            el.displayName,
            el.booking_number,
            el.badge_number,
            el.number_of_users,
            el.has_checkin,
            el.is_waiting_list,
            el.id_cabin
          ));

          this.setDatasource();
        },
        error: (err) => {
          this.showError(err);
          this.spinner.hide();
        },
      });

  }

  private setDatasource() {
    this.dataSource.data = this.allData_event;
    this.viewReservations = true;
    this.btnDownload = this.allData_event.length > 0;
    this.spinner.hide();
  }

  isSearchDisabled() {
    return !this.eventSelected;
  }

  downloadFile() {
    const myworksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet([]);
    XLSX.utils.sheet_add_aoa(myworksheet, this.xlsxColumns);

    const sortedHeader = [
      'event_name',
      'creation_datetime',
      'occurrence_datetime',
      'displayName',
      'booking_number',
      'badge_number',
      'id_cabin',
      'number_of_users',
      'has_checkin',
    ];
    XLSX.utils.sheet_add_json(myworksheet, this.allData_event, {
      origin: 'A2',
      header: sortedHeader,
      skipHeader: true,
    });

    const myworkbook: XLSX.WorkBook = {
      Sheets: { Report: myworksheet },
      SheetNames: ['Report'],
    };

    XLSX.writeFile(
      myworkbook,
      'WRT_Costa_Prenotazioni_'+ (this.selectedShip ? this.selectedShip.ship_name.replace(" ","_") : "") + "_" + this.event_name_after_search + '.xlsx'
    );
  }

  private showError(err: any) {
    const dialogRef = this.dialog.open(ModalErrorComponent, {
      data: MyErrorHandler.getText(err),
    });

    dialogRef.afterClosed().subscribe(() => {
      this.goTo('landing');
    });
  }

  goTo(path: string) {
    switch (path) {
      case 'home':
        this.router.navigate([ROUTES.HOME.path]);
        break;
      case 'landing':
        this.router.navigate([ROUTES.RESERVATION.path]);
        break;
    }
  }
}
